import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useDocumentTitle(query ? `搜索「${query}」 - 交换朋友的妻子` : "站内搜索 - 交换朋友的妻子");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return movies.filter((m) => {
      const haystack = [
        m.title,
        m.originalTitle,
        m.director,
        m.region,
        ...m.genres,
        ...m.tags,
        ...m.cast.map((c) => c.name),
        ...m.cast.map((c) => c.role),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="bg-background-950 pt-24 md:pt-28 pb-16 md:pb-20">
      <div className="mx-auto max-w-site px-4 md:px-6">
        <nav className="text-xs text-foreground-500 mb-5 flex items-center gap-2">
          <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-300">站内搜索</span>
        </nav>

        <header className="max-w-3xl mb-8">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-50">影视内容搜索</h1>
          <p className="mt-3 text-sm text-foreground-400">
            输入片名、演员名或题材关键词，即可找到相关影片。
          </p>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-2xl mb-10">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="例如：交换朋友的妻子、婚姻、悬疑"
              className="w-full rounded-md bg-background-900 border border-background-800 pl-11 pr-4 py-3 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400"
            />
          </div>
          <button
            type="submit"
            className="px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </form>

        {query && (
          <p className="text-sm text-foreground-400 mb-6">
            关键词「<span className="text-accent-400">{query}</span>」共找到{" "}
            <span className="text-accent-400 font-label">{results.length}</span> 条结果
          </p>
        )}

        {!query ? (
          <div className="rounded-lg border border-dashed border-background-700 p-12 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-900 text-3xl text-foreground-500">
              <i className="ri-search-eye-line"></i>
            </span>
            <p className="mt-4 text-foreground-300">还没有开始搜索</p>
            <p className="mt-2 text-sm text-foreground-500">在上方输入关键词，寻找你感兴趣的影片。</p>
          </div>
        ) : results.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-700 p-12 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-900 text-3xl text-foreground-500">
              <i className="ri-emotion-sad-line"></i>
            </span>
            <p className="mt-4 text-foreground-300">没有找到与「{query}」相关的内容</p>
            <p className="mt-2 text-sm text-foreground-500">
              试试更换关键词，例如影片名或演员名，也可以先清空后重新搜索。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams({});
                }}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                清空关键词
              </button>
              <Link
                to="/category"
                className="px-5 py-2.5 rounded-md border border-background-600 text-foreground-100 text-sm font-medium hover:border-accent-500 hover:text-accent-400 transition-colors"
              >
                浏览全部影片
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {results.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}